import { __decorate } from "tslib";
import { action, computed, get, isObservableObject, observable, remove, runInAction, set, toJS } from 'mobx';
import raf from 'raf';
import isString from 'lodash/isString';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import merge from 'lodash/merge';
import { getDateFormatByField, isSame, isSameLike, MAX_SAFE_INTEGER, MIN_SAFE_INTEGER, warning } from '../utils';
import DataSet from './DataSet';
import Validator from '../validator/Validator';
import { DataSetEvents, DataSetSelection, DataSetStatus, FieldTrim, FieldType } from './enum';
import lookupStore from '../stores/LookupCodeStore';
import lovCodeStore from '../stores/LovCodeStore';
import attachmentStore from '../stores/AttachmentStore';
import localeContext from '../locale-context';
import { defaultTextField, defaultValueField, getBaseType, getChainFieldName, getIf, getIfForMap, getLimit, mergeDataSetProps } from './utils';
import { buildURLWithAxiosConfig } from '../axios/utils';
import { getLovPara } from '../stores/utils';
import AttachmentFile from './AttachmentFile';
import { iteratorFind, iteratorSome } from '../iterator-helper';
import LookupCache from './LookupCache';
function isEqualDynamicProps(oldProps, newProps) {
    if (newProps === oldProps) {
        return true;
    }
    if (isObject(newProps) && isObject(oldProps)) {
        const newKeys = Object.keys(newProps);
        const { length } = newKeys;
        if (length) {
            if (length !== Object.keys(oldProps).length) {
                return false;
            }
            return newKeys.every(key => {
                const value = newProps[key];
                const oldValue = oldProps[key];
                if (oldValue === value) {
                    return true;
                }
                if (typeof value === 'function' && typeof oldValue === 'function') {
                    return value.toString() === oldValue.toString();
                }
                return isEqual(oldValue, value);
            });
        }
    }
    return isEqual(newProps, oldProps);
}
function getPropsFromLovConfig(lovCode, propsName) {
    const props = {};
    if (lovCode) {
        const config = lovCodeStore.getConfig(lovCode);
        if (config) {
            propsName.forEach(name => {
                const value = config[name];
                if (value) {
                    props[name] = value;
                }
            });
        }
    }
    return props;
}
const LOOKUP_SIDE_EFFECT_KEYS = [
    'type',
    'lookupUrl',
    'lookupCode',
    'lookupAxiosConfig',
    'lovCode',
    'lovQueryAxiosConfig',
    'lovPara',
    'cascadeMap',
    'lovQueryUrl',
    'optionsProps',
];
const LOV_SIDE_EFFECT_KEYS = ['lovCode', 'lovDefineAxiosConfig', 'lovDefineUrl', 'optionsProps'];
const LOOKUP_DATA = 'lookupData';
function getLookupToken(field, record) {
    if (record) {
        const { lookupTokens } = record;
        if (lookupTokens) {
            const { name } = field;
            if (lookupTokens.has(name)) {
                return lookupTokens.get(name);
            }
        }
        if (field.record) {
            const dsField = record.dataSet.getField(field.name);
            return dsField && dsField.lookupToken;
        }
    }
    return field.lookupToken;
}
function setLookupToken(field, token, record) {
    if (record) {
        if (token !== undefined || record.lookupTokens) {
            const lookupTokens = getIf(record, 'lookupTokens', () => observable.map());
            lookupTokens.set(field.name, token);
        }
    }
    else if (token !== field.lookupToken) {
        field.lookupToken = token;
    }
}
function combineWithOldLookupData(lookup, field, record) {
    const lookupData = field.get(LOOKUP_DATA, record);
    if (lookupData) {
        const valueField = field.get('valueField', record);
        const others = lookupData.filter((data) => lookup.every(item => item[valueField] !== data[valueField]));
        if (others.length) {
            return others.concat(lookup.slice());
        }
    }
    return lookup;
}
const defaultProps = {
    type: FieldType.auto,
    required: false,
    readOnly: false,
    disabled: false,
    group: false,
    textField: defaultTextField,
    valueField: defaultValueField,
    trueValue: true,
    falseValue: false,
    trim: FieldTrim.both,
};
export default class Field {
    constructor(props = {}, dataSet, record) {
        runInAction(() => {
            this.dataSet = dataSet;
            this.record = record;
            this.props = observable.map(props);
            if (!props.options) {
                const { dynamicProps, computedProps } = props;
                if (typeof dynamicProps === 'function') {
                    this.hasLookupSideEffects = true;
                    this.hasLovSideEffects = true;
                }
                else {
                    const keys = Object.keys({ ...computedProps, ...dynamicProps });
                    if (keys.length) {
                        this.hasLookupSideEffects = keys.some(key => LOOKUP_SIDE_EFFECT_KEYS.includes(key));
                        this.hasLovSideEffects = keys.some(key => LOV_SIDE_EFFECT_KEYS.includes(key));
                    }
                }
                if (record) {
                    const dsField = dataSet.getField(props.name);
                    if (dsField) {
                        if (!this.hasLookupSideEffects) {
                            this.hasLookupSideEffects = dsField.hasLookupSideEffects;
                        }
                        if (!this.hasLovSideEffects) {
                            this.hasLovSideEffects = dsField.hasLovSideEffects;
                        }
                    }
                    this.processForLookupAndLovConfig(record);
                }
                else {
                    raf(() => {
                        this.fetchLookup();
                        this.fetchLovConfig();
                    });
                }
            }
        });
    }
    get attachments() {
        return this.getAttachments();
    }
    set attachments(attachments) {
        this.setAttachments(attachments);
    }
    get attachmentCount() {
        return this.getAttachmentCount();
    }
    set attachmentCount(count) {
        this.setAttachmentCount(count);
    }
    get pristineProps() {
        return {
            ...this.props.toPOJO(),
            ...this.dirtyProps,
        };
    }
    set pristineProps(props) {
        runInAction(() => {
            const { dirtyProps } = this;
            if (dirtyProps) {
                const newProps = {};
                const dirtyKeys = Object.keys(dirtyProps);
                if (dirtyKeys.length) {
                    dirtyKeys.forEach((key) => {
                        const item = this.props.get(key);
                        newProps[key] = item;
                        if (isSame(item, props[key])) {
                            delete dirtyProps[key];
                        }
                        else {
                            dirtyProps[key] = props[key];
                        }
                    });
                }
                this.props.replace({
                    ...props,
                    ...newProps,
                });
            }
            else {
                this.props.replace(props);
            }
        });
    }
    get lookup() {
        return this.getLookup();
    }
    get options() {
        return this.getOptions();
    }
    get dirty() {
        return this.isDirty();
    }
    get name() {
        return this.props.get('name');
    }
    get order() {
        return this.get('order');
    }
    set order(order) {
        this.set('order', order);
    }
    get valid() {
        return this.isValid();
    }
    get validationMessage() {
        return this.getValidationMessage();
    }
    processForLookupAndLovConfig(record) {
        const { hasLovSideEffects, hasLookupSideEffects } = this;
        if (hasLovSideEffects || hasLookupSideEffects) {
            raf(() => {
                if (hasLookupSideEffects) {
                    this.fetchLookup(undefined, record);
                }
                if (hasLovSideEffects) {
                    this.fetchLovConfig(record);
                }
            });
        }
    }
    isDirty(record = this.record) {
        if (record) {
            const { name } = this;
            const { dirtyData } = record;
            if (dirtyData && dirtyData.size) {
                const chainFieldName = getChainFieldName(record, name);
                if (dirtyData.has(chainFieldName) || iteratorSome(dirtyData.keys(), (key) => chainFieldName.startsWith(`${key}.`))) {
                    return true;
                }
                if (this.get('type', record) === FieldType.intl) {
                    const tlsKey = this.dataSet.getConfig('tlsKey');
                    if (record.get(tlsKey) && Object.keys(localeContext.supports).some(lang => dirtyData.has(getChainFieldName(record, `${tlsKey}.${name}.${lang}`)))) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    /**
     * 获取所有属性
     * @return 属性对象
     */
    getProps(record = this.record) {
        const dsField = this.findDataSetField();
        const lovCode = this.get('lovCode', record);
        return merge({ lookupUrl: this.dataSet.getConfig('lookupUrl') }, defaultProps, getPropsFromLovConfig(lovCode, ['textField', 'valueField']), dsField && dsField.props.toPOJO(), this.props.toPOJO());
    }
    /**
     * 根据属性名获取属性值
     * @param propsName 属性名
     * @return {any}
     */
    get(propsName, record = this.record) {
        const prop = this.getProp(propsName, record);
        if (prop !== undefined) {
            return prop;
        }
        return defaultProps[propsName];
    }
    getProp(propsName, record = this.record) {
        if (record && !this.record) {
            const recordField = record.ownerFields.get(this.name);
            if (recordField) {
                const recordProp = recordField.getProp(propsName);
                if (recordProp !== undefined) {
                    return recordProp;
                }
            }
        }
        if (!['computedProps', 'dynamicProps'].includes(propsName)) {
            const computedPropsMap = record ?
                getIf(record, 'computedFieldProps', () => new Map()) :
                getIf(this, 'computedProps', () => new Map());
            const computedKey = `__${this.name}$${propsName}__`;
            const computedProp = computedPropsMap.get(computedKey);
            if (computedProp) {
                const computedValue = computedProp.get();
                if (computedValue !== undefined) {
                    return computedValue;
                }
            }
            else {
                const computedProps = this.get('computedProps', record);
                if (computedProps) {
                    const newComputedProp = computed(() => {
                        const computProp = computedProps[propsName];
                        if (typeof computProp === 'function') {
                            const prop = this.executeDynamicProps(computProp, propsName, record);
                            if (prop !== undefined) {
                                this.checkDynamicProp(propsName, prop, record);
                                return prop;
                            }
                        }
                    }, { name: computedKey, context: record || this });
                    computedPropsMap.set(computedKey, newComputedProp);
                    const computedValue = newComputedProp.get();
                    if (computedValue !== undefined) {
                        return computedValue;
                    }
                }
            }
            const dynamicProps = this.get('dynamicProps', record);
            if (dynamicProps) {
                if (typeof dynamicProps === 'function') {
                    warning(false, ` The dynamicProps hook will be deprecated. Please use dynamicProps map.
              For e.g,
              Bad case:
              dynamicProps({ record }) {
                return {
                  bind: record.get('xx'),
                  label: record.get('yy'),
                }
              }
              Good case:
              dynamicProps = {
                bind({ record }) {
                  return record.get('xx')
                },
                label({ record }) {
                  return record.get('yy'),
                }
              }`);
                    const props = this.executeDynamicProps(dynamicProps, propsName, record);
                    if (props && propsName in props) {
                        const prop = props[propsName];
                        this.checkDynamicProp(propsName, prop, record);
                        return prop;
                    }
                }
                else {
                    const dynamicProp = dynamicProps[propsName];
                    if (typeof dynamicProp === 'function') {
                        const prop = this.executeDynamicProps(dynamicProp, propsName, record);
                        if (prop !== undefined) {
                            this.checkDynamicProp(propsName, prop, record);
                            return prop;
                        }
                    }
                }
                this.checkDynamicProp(propsName, undefined, record);
            }
        }
        const value = this.props.get(propsName);
        if (value !== undefined) {
            return value;
        }
        if (propsName === 'lookup') {
            const { dataSet } = this;
            const lookupToken = getLookupToken(this, record);
            const { lookupCaches } = dataSet;
            if (lookupToken && lookupCaches) {
                const lookup = lookupCaches.get(lookupToken);
                if (lookup) {
                    const { items } = lookup;
                    if (items) {
                        return items;
                    }
                }
                return undefined;
            }
        }
        const dsField = this.findDataSetField();
        if (dsField) {
            const dsValue = dsField.getProp(propsName);
            if (dsValue !== undefined) {
                return dsValue;
            }
        }
        if (propsName === 'textField' || propsName === 'valueField') {
            const lovCode = this.get('lovCode', record);
            const lovProp = getPropsFromLovConfig(lovCode, [propsName])[propsName];
            if (lovProp) {
                return lovProp;
            }
        }
        if (propsName === 'lookupUrl') {
            return this.dataSet.getConfig('lookupUrl');
        }
        if (['min', 'max'].includes(propsName)) {
            if (this.get('type', record) === FieldType.number) {
                if (propsName === 'max') {
                    return MAX_SAFE_INTEGER;
                }
                return MIN_SAFE_INTEGER;
            }
        }
        return undefined;
    }
    /**
     * 设置属性值
     * @param propsName 属性名
     * @param value 属性值
     * @return {any}
     */
    set(propsName, value) {
        const { record, name } = this;
        if (record) {
            const recordField = record.ownerFields.get(name);
            if (recordField) {
                if (recordField !== this) {
                    recordField.set(propsName, value);
                    return recordField;
                }
            }
            else {
                record.ownerFields.set(name, this);
            }
        }
        const oldValue = this.get(propsName);
        if (!isEqualDynamicProps(toJS(oldValue), value)) {
            const dirtyProps = getIf(this, 'dirtyProps', {});
            if (!(propsName in dirtyProps)) {
                set(dirtyProps, propsName, oldValue);
            }
            else if (isSame(toJS(dirtyProps[propsName]), value)) {
                remove(dirtyProps, propsName);
            }
            this.props.set(propsName, value);
            const { dataSet } = this;
            dataSet.fireEvent(DataSetEvents.fieldChange, {
                dataSet,
                record,
                name,
                field: this,
                propsName,
                value,
                oldValue,
            });
            this.handlePropChange(propsName, value, oldValue, record);
            if (!record) {
                dataSet.forEach((r) => {
                    const recordField = r.ownerFields.get(name);
                    if (!recordField || !recordField.props.has(propsName)) {
                        this.handlePropChange(propsName, value, oldValue, r);
                    }
                });
            }
        }
        return this;
    }
    replace(props = {}) {
        const p = { ...props };
        this.props.forEach((_v, key) => {
            if (![LOOKUP_DATA, 'name'].includes(key)) {
                this.set(key, props[key]);
            }
            delete p[key];
        });
        return this.merge(p);
    }
    merge(props) {
        Object.keys(props).forEach((key) => {
            if (key !== 'name') {
                this.set(key, props[key]);
            }
        });
        return this;
    }
    getLookup(record = this.record) {
        const lookupToken = getLookupToken(this, record);
        const { lookupCaches } = this.dataSet;
        if (lookupToken && lookupCaches) {
            const lookup = lookupCaches.get(lookupToken);
            if (lookup) {
                const { items } = lookup;
                if (items) {
                    return combineWithOldLookupData(items, this, record);
                }
            }
        }
    }
    /**
     * 根据lookup值获取lookup对象
     * @param value lookup值
     * @return {object}
     */
    getLookupData(value, record = this.record) {
        value = value === undefined ? this.getValue(record) : value;
        const valueField = this.get('valueField', record);
        const data = {};
        const lookup = this.getLookup(record);
        if (lookup) {
            return lookup.find(obj => isSameLike(get(obj, valueField), value)) || data;
        }
        return data;
    }
    getValue(record = this.record) {
        const r = record || this.dataSet.current;
        if (r) {
            return r.get(this.name);
        }
    }
    /**
     * 可以根据lookup值获取含义
     * @param value lookup值
     * @param boolean showValueIfNotFound
     * @return {string}
     */
    getLookupText(value, showValueIfNotFound, record = this.record) {
        value = value === undefined ? this.getValue(record) : value;
        const textField = this.get('textField', record);
        const valueField = this.get('valueField', record);
        const lookup = this.getLookup(record);
        if (lookup) {
            const found = lookup.find(obj => isSameLike(get(obj, valueField), value));
            if (found) {
                return get(found, textField);
            }
            if (showValueIfNotFound) {
                return value;
            }
            return undefined;
        }
    }
    /**
     * 可以根据options值获取含义
     * @param value opions值
     * @param boolean showValueIfNotFound
     * @return {string}
     */
    getOptionsText(value, showValueIfNotFound, record = this.record) {
        value = value === undefined ? this.getValue(record) : value;
        const textField = this.get('textField', record);
        const valueField = this.get('valueField', record);
        const options = this.getOptions(record);
        if (options) {
            const found = options.find(item => isSameLike(item.get(valueField), value));
            if (found) {
                return found.get(textField);
            }
            if (showValueIfNotFound) {
                return value;
            }
            return undefined;
        }
    }
    /**
     * 根据lookup值获取lookup含义
     * @param value lookup值
     * @param boolean showValueIfNotFound
     * @return {string}
     */
    getText(value, showValueIfNotFound, record = this.record) {
        value = value === undefined ? this.getValue(record) : value;
        const lookup = this.getLookup(record);
        if (lookup && !isObject(value)) {
            return this.getLookupText(value, showValueIfNotFound, record);
        }
        const options = this.get('options', record);
        const textField = this.get('textField', record);
        if (options) {
            const valueField = this.get('valueField', record);
            const found = options.find(item => isSameLike(item.get(valueField), value));
            if (found) {
                return found.get(textField, record);
            }
        }
        if (textField && isObject(value)) {
            if (isObservableObject(value)) {
                return get(value, textField);
            }
            return value[textField];
        }
        return value;
    }
    setOptions(options) {
        this.set('options', options);
    }
    getOptions(record = this.record) {
        const options = this.get('options', record);
        if (options) {
            return options;
        }
        const { name } = this;
        const computedOptionsMap = record ? getIf(record, 'computedFieldOptions', () => new Map()) : undefined;
        const computedOptions = computedOptionsMap ? computedOptionsMap.get(name) :
            this.computedOptions;
        if (computedOptions) {
            return computedOptions.get();
        }
        const newComputedOptions = computed(() => {
            // 确保 lookup 相关配置介入观察
            lookupStore.getAxiosConfig(this, record);
            const optionsProps = this.get('optionsProps', record);
            const lookupToken = getLookupToken(this, record);
            const type = this.get('type', record);
            if (lookupToken) {
                const { lookupCaches } = this.dataSet;
                if (lookupCaches) {
                    const lookup = lookupCaches.get(lookupToken);
                    if (lookup) {
                        const { items, promise } = lookup;
                        if (items) {
                            const parentField = this.get('parentField', record);
                            const idField = this.get('idField', record) || this.get('valueField', record);
                            const selection = this.get('multiple', record) ? DataSetSelection.multiple : DataSetSelection.single;
                            const props = {
                                data: combineWithOldLookupData(items, this, record),
                                paging: false,
                                selection,
                                idField,
                                parentField,
                                status: promise ? DataSetStatus.loading : DataSetStatus.ready,
                            };
                            return new DataSet(mergeDataSetProps(props, optionsProps));
                        }
                        if (promise) {
                            return new DataSet({ status: DataSetStatus.loading });
                        }
                    }
                }
            }
            const lovCode = this.get('lovCode', record);
            if (lovCode) {
                if (type === FieldType.object || type === FieldType.auto) {
                    return lovCodeStore.getLovDataSet(lovCode, this, optionsProps, record);
                }
            }
            return undefined;
        });
        if (computedOptionsMap) {
            computedOptionsMap.set(name, newComputedOptions);
        }
        else {
            this.computedOptions = newComputedOptions;
        }
        return newComputedOptions.get();
    }
    /**
     * 重置设置的属性
     */
    reset() {
        const { dirtyProps } = this;
        if (dirtyProps) {
            this.props.merge(dirtyProps);
            this.dirtyProps = undefined;
        }
    }
    commit(record = this.record) {
        if (record) {
            record.clearValidationError(this.name);
        }
    }
    /**
     * 是否必选
     * @return true | false
     */
    get required() {
        return this.get('required');
    }
    /**
     * 设置是否必选
     * @param required 是否必选
     */
    set required(required) {
        this.set('required', required);
    }
    /**
     * 是否只读
     * @return true | false
     */
    get readOnly() {
        return this.get('readOnly');
    }
    /**
     * 设置是否只读
     * @param readOnly 是否只读
     */
    set readOnly(readOnly) {
        this.set('readOnly', readOnly);
    }
    /**
     * 是否禁用
     * @return true | false
     */
    get disabled() {
        return this.get('disabled');
    }
    /**
     * 设置是否禁用
     * @param disabled 是否禁用
     */
    set disabled(disabled) {
        this.set('disabled', disabled);
    }
    /**
     * 获取字段类型
     * @return 获取字段类型
     */
    get type() {
        return this.get('type');
    }
    /**
     * 设置字段类型
     * @param type 字段类型
     */
    set type(type) {
        this.set('type', type);
    }
    /**
     * 设置Lov的查询参数
     * @param {String} name
     * @param {Object} value
     */
    setLovPara(name, value, record = this.record) {
        const p = toJS(this.get('lovPara', record)) || {};
        if (value === null) {
            delete p[name];
        }
        else {
            p[name] = value;
        }
        this.set('lovPara', p);
    }
    getValidatorPropGetter(record = this.record) {
        return (key) => {
            if (record) {
                switch (key) {
                    case 'attachmentCount': {
                        const attachments = this.getAttachments(record);
                        const validAttachments = attachments && attachments.filter(({ status }) => !status || ['success', 'done'].includes(status));
                        return validAttachments ? validAttachments.length : this.getAttachmentCount(record);
                    }
                    case 'max':
                    case 'min':
                        return getLimit(this.get(key, record), record);
                    case 'minLength':
                    case 'maxLength':
                        return getBaseType(this.get('type', record)) !== FieldType.string ? undefined : this.get(key, record);
                    case 'format':
                        return this.get('format', record) || getDateFormatByField(this, this.get('type', record), record);
                    case 'nonStrictStep': {
                        const nonStrictStep = this.get('nonStrictStep', record);
                        if (nonStrictStep === undefined) {
                            return this.dataSet.getConfig('numberFieldNonStrictStep');
                        }
                        return nonStrictStep;
                    }
                    case 'customValidator':
                        return this.get('validator', record);
                    case 'defaultValidationMessages':
                        return {
                            ...this.dataSet.getConfig('defaultValidationMessages'),
                            ...this.get('defaultValidationMessages', record),
                        };
                    case 'stringMode':
                        return this.get('type', record) === FieldType.bigNumber;
                    default:
                        return this.get(key, record);
                }
            }
        };
    }
    /**
     * 校验字段值
     * 只有通过record.getField()获取的field才能校验
     * @return true | false
     */
    async checkValidity(record = this.record) {
        if (record) {
            const { name } = this;
            const { valid, validationResults } = await Validator.checkValidity(record.get(name), {
                dataSet: record.dataSet,
                name,
                record,
            }, this.getValidatorPropGetter(record));
            record.setValidationError(name, validationResults);
            if (!record.validating) {
                record.reportValidity({
                    field: this,
                    errors: validationResults,
                    valid,
                }, true);
            }
            return valid;
        }
        return true;
    }
    /**
     * 请求lookup值, 如有缓存值直接获得。
     * @param noCache default: undefined
     * @return Promise<object[]>
     */
    fetchLookup(noCache = false, record = this.record) {
        const lookupCaches = getIf(this.dataSet, 'lookupCaches', () => observable.map());
        const oldToken = getLookupToken(this, record);
        const batch = this.get('lookupBatchAxiosConfig', record) || this.dataSet.getConfig('lookupBatchAxiosConfig');
        const lookupCode = this.get('lookupCode', record);
        let promise;
        if (batch && lookupCode && Object.keys(getLovPara(this, record)).length === 0) {
            const cachedLookup = getIfForMap(lookupCaches, lookupCode, () => new LookupCache());
            if (lookupCode !== oldToken) {
                setLookupToken(this, lookupCode, record);
            }
            const { items } = cachedLookup;
            if (items) {
                promise = Promise.resolve(items);
            }
            else {
                promise = cachedLookup.promise;
            }
            if (!promise) {
                promise = lookupStore.fetchLookupDataInBatch(lookupCode, batch).then(action((result) => {
                    if (result) {
                        cachedLookup.items = result;
                        cachedLookup.promise = undefined;
                    }
                    return result;
                }));
                cachedLookup.promise = promise;
            }
        }
        else {
            const axiosConfig = lookupStore.getAxiosConfig(this, record, noCache);
            if (axiosConfig.url) {
                const lookupToken = buildURLWithAxiosConfig(axiosConfig);
                if (lookupToken !== oldToken) {
                    setLookupToken(this, lookupToken, record);
                }
                const cachedLookup = getIfForMap(lookupCaches, lookupToken, () => new LookupCache());
                if (!noCache) {
                    const { items } = cachedLookup;
                    if (items) {
                        promise = Promise.resolve(items);
                    }
                    else {
                        promise = cachedLookup.promise;
                    }
                }
                if (!promise) {
                    promise = lookupStore.fetchLookupData(axiosConfig, undefined, this).then(action((result) => {
                        if (result) {
                            cachedLookup.items = result;
                            cachedLookup.promise = undefined;
                        }
                        return result;
                    }));
                    cachedLookup.promise = promise;
                }
            }
            else {
                setLookupToken(this, undefined, record);
            }
        }
        if (promise) {
            return promise.then(action((result) => {
                const lookup = oldToken ? lookupCaches.get(oldToken) : undefined;
                if (lookup) {
                    const { items } = lookup;
                    if (items && oldToken !== getLookupToken(this, record)) {
                        const value = this.getValue(record);
                        const valueField = this.get('valueField', record);
                        if (value && valueField) {
                            const values = this.get('multiple', record) ? [].concat(...value) : [].concat(value);
                            this.set(LOOKUP_DATA, values.reduce((lookupData, v) => {
                                const found = items.find(item => isSameLike(item[valueField], v));
                                if (found) {
                                    lookupData.push(found);
                                }
                                return lookupData;
                            }, []));
                        }
                    }
                }
                return toJS(result);
            }));
        }
        return Promise.resolve(undefined);
    }
    fetchLovConfig(record = this.record) {
        const lovCode = this.get('lovCode', record);
        if (lovCode) {
            lovCodeStore.fetchConfig(lovCode, this, record);
        }
    }
    fetchAttachments(props, record = this.record) {
        const { bucketName, bucketDirectory, attachmentUUID, storageCode, isPublic } = props;
        const { fetchList } = this.dataSet.getConfig('attachment');
        if (fetchList) {
            fetchList({ bucketName, bucketDirectory, attachmentUUID, storageCode, isPublic }).then(action((results) => {
                this.setAttachments(results.map(file => new AttachmentFile(file)), record, undefined);
            }));
        }
    }
    fetchAttachmentCount(uuid, isPublic, record = this.record) {
        const { batchFetchCount } = this.dataSet.getConfig('attachment');
        if (batchFetchCount && !this.attachments) {
            attachmentStore.fetchCountInBatch(uuid, this, isPublic).then((count) => {
                this.setAttachmentCount(count, record);
            });
        }
    }
    isValid(record = this.record) {
        if (record) {
            const results = this.getValidationErrorValues(record);
            return !results || !results.length;
        }
        return true;
    }
    getValidationMessage(record = this.record) {
        if (record) {
            const results = this.getValidationErrorValues(record);
            if (results && results.length) {
                return results[0].validationMessage;
            }
        }
        return undefined;
    }
    getValidationErrorValues(record = this.record) {
        if (record) {
            const errors = record.getValidationError(this.name);
            if (errors && errors.length) {
                return errors;
            }
            const unique = this.get('unique', record);
            if (isString(unique)) {
                const { validationErrors } = record;
                if (validationErrors) {
                    const uniqueErrors = iteratorFind(validationErrors.values(), (errors) => errors.some(error => error.ruleName === 'uniqueError' && error.validationProps.unique === unique));
                    if (uniqueErrors) {
                        return uniqueErrors;
                    }
                }
            }
        }
        return [];
    }
    setAttachments(attachments, record = this.record, uuid) {
        if (record) {
            const value = uuid || record.get(this.name);
            if (value) {
                const attachmentCaches = getIf(record, 'attachmentCaches', () => observable.map());
                const cache = attachmentCaches.get(value);
                if (cache) {
                    set(cache, 'attachments', attachments);
                }
                else {
                    attachmentCaches.set(value, { attachments });
                }
            }
        }
        else {
            this.set('attachments', attachments);
        }
    }
    getAttachments(record = this.record) {
        if (record) {
            const uuid = record.get(this.name);
            if (uuid) {
                const { attachmentCaches } = record;
                if (attachmentCaches) {
                    const cache = attachmentCaches.get(uuid);
                    if (cache) {
                        return get(cache, 'attachments');
                    }
                }
            }
        }
        else {
            return this.get('attachments');
        }
        return undefined;
    }
    setAttachmentCount(count, record = this.record) {
        if (record) {
            const uuid = record.get(this.name);
            if (uuid) {
                const attachmentCaches = getIf(record, 'attachmentCaches', () => observable.map());
                const cache = attachmentCaches.get(uuid);
                if (cache) {
                    set(cache, 'count', count);
                }
                else {
                    attachmentCaches.set(uuid, { count });
                }
            }
        }
        else {
            this.set('attachmentCount', count);
        }
    }
    getAttachmentCount(record = this.record) {
        const attachments = this.getAttachments(record);
        if (attachments) {
            return attachments.length;
        }
        if (record) {
            const uuid = record.get(this.name);
            if (uuid) {
                const { attachmentCaches } = record;
                if (attachmentCaches) {
                    const cache = attachmentCaches.get(uuid);
                    if (cache) {
                        return get(cache, 'count');
                    }
                }
            }
        }
        else {
            return this.get('attachmentCount');
        }
        return undefined;
    }
    ready() {
        return Promise.resolve(true);
    }
    checkDynamicProp(propsName, newProp, record = this.record) {
        const { name } = this;
        const lastDynamicProps = record ? (() => {
            const lastDynamicFieldProps = getIf(record, 'lastDynamicFieldProps', () => new Map());
            let last = lastDynamicFieldProps.get(name);
            if (!last) {
                last = {};
                lastDynamicFieldProps.set(name, last);
            }
            return last;
        })() : getIf(this, 'lastDynamicProps', {});
        const oldProp = lastDynamicProps[propsName];
        if (!isEqualDynamicProps(oldProp, newProp)) {
            raf(action(() => {
                if (record && oldProp !== undefined) {
                    const { name } = this;
                    const errors = record.getValidationError(name);
                    if (errors) {
                        if (propsName === 'validator') {
                            record.clearValidationError(name);
                        }
                        else if (!['label', 'defaultValidationMessages'].includes(propsName)) {
                            errors.some(error => {
                                const { validationProps } = error;
                                if (validationProps && Object.keys(validationProps).includes(propsName)) {
                                    record.clearValidationError(name);
                                    return true;
                                }
                                return false;
                            });
                        }
                    }
                }
                this.handlePropChange(propsName, newProp, oldProp, record);
            }));
        }
        lastDynamicProps[propsName] = newProp;
    }
    handlePropChange(propsName, newProp, oldProp, record = this.record) {
        if (propsName === 'bind' && this.get('type', record) !== FieldType.intl) {
            if (record && !this.isDirty(record)) {
                if (newProp && oldProp) {
                    record.init(newProp, record.get(oldProp));
                }
                if (oldProp) {
                    record.init(oldProp, undefined);
                }
            }
            return;
        }
        if (LOOKUP_SIDE_EFFECT_KEYS.includes(propsName) ||
            (['dynamicProps', 'computedProps'].includes(propsName) &&
                (typeof newProp === 'function' || typeof oldProp === 'function' ||
                    (newProp && Object.keys(newProp).some(key => LOOKUP_SIDE_EFFECT_KEYS.includes(key))) ||
                    (oldProp && Object.keys(oldProp).some(key => LOOKUP_SIDE_EFFECT_KEYS.includes(key)))))) {
            this.set(LOOKUP_DATA, undefined);
            this.fetchLookup(undefined, record);
        }
        if (LOV_SIDE_EFFECT_KEYS.includes(propsName) ||
            (['dynamicProps', 'computedProps'].includes(propsName) &&
                (typeof newProp === 'function' || typeof oldProp === 'function' ||
                    (newProp && Object.keys(newProp).some(key => LOV_SIDE_EFFECT_KEYS.includes(key))) ||
                    (oldProp && Object.keys(oldProp).some(key => LOV_SIDE_EFFECT_KEYS.includes(key)))))) {
            this.fetchLovConfig(record);
        }
    }
    executeDynamicProps(dynamicProps, propsName, record = this.record) {
        const { dataSet, name } = this;
        const dynamicPropsComputingChains = getIf(this, 'dynamicPropsComputingChains', []);
        if (dynamicPropsComputingChains.includes(propsName)) {
            warning(false, `Cycle dynamicProps execution of field<${name}>. [${dynamicPropsComputingChains.join(' -> ')} -> ${propsName}]`);
        }
        else {
            dynamicPropsComputingChains.push(propsName);
            try {
                return dynamicProps({ dataSet, record, name });
            }
            catch (e) {
                if (record) {
                    throw e;
                }
                if (process.env.NODE_ENV !== 'production') {
                    console.warn(e);
                }
            }
            finally {
                dynamicPropsComputingChains.pop();
            }
        }
    }
    findDataSetField() {
        const { dataSet, name, record } = this;
        if (record && dataSet && name) {
            return dataSet.getField(name);
        }
    }
}
Field.defaultProps = defaultProps;
__decorate([
    observable
], Field.prototype, "lookupToken", void 0);
__decorate([
    observable
], Field.prototype, "props", void 0);
__decorate([
    observable
], Field.prototype, "dirtyProps", void 0);
__decorate([
    action
], Field.prototype, "set", null);
__decorate([
    action
], Field.prototype, "replace", null);
__decorate([
    action
], Field.prototype, "merge", null);
__decorate([
    action
], Field.prototype, "reset", null);
__decorate([
    action
], Field.prototype, "commit", null);
__decorate([
    action
], Field.prototype, "setLovPara", null);
__decorate([
    action
], Field.prototype, "checkValidity", null);
__decorate([
    action
], Field.prototype, "fetchLookup", null);
__decorate([
    action
], Field.prototype, "setAttachments", null);
__decorate([
    action
], Field.prototype, "setAttachmentCount", null);
